import React, { useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TreeSelect } from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { fetchUnpaidInvoices } from '@store/Finance/AP/ApInvoices/UnpaidInvoicesSlice';
import { type UnPaymentSchedulesModels } from '@store/Finance/AP/ApInvoices/ApInvoicesModels';
import { Card } from '@components/card/card';
import useGetUnApplyPrepayments from '@hooks/Finance/ApPayments/useGetUnApplyPrepayments';
import Loading from '@components/Shared/Loader/Loading';
import { NumericFormat } from 'react-number-format';



export const StandardPaymentComponent: React.FC<{
  formik: any;
  onClose: () => void;
  OperatingUnitOptions: any[];
  treeSuppliersData: any[];
}> = ({ formik, onClose, OperatingUnitOptions, treeSuppliersData }) => {
  const { values, setFieldValue } = formik;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const { t, i18n } = useTranslation();

  const [invoices, setInvoices] = useState<UnPaymentSchedulesModels[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<{ id: any; amount: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedInvoices.length > 0) {
      setSelectedInvoices([]);
      setInvoices([]);
      setFetchError(null);
    }
  }, [values.peopleId, values.organizationId]);

  const handleGetUnpaidInvoices = async () => {
    if (!values.organizationId || !values.peopleId) return;

    setIsLoading(true);
    setFetchError(null);

    try {
      const result = await dispatch(
        fetchUnpaidInvoices({
          peopleId: values.peopleId,
          organizationId: values.organizationId,
          invoiceType: 'Standard'
        })
      ).unwrap();

      if (result.succeeded) {
        const invoicesWithType = result.data.map((invoice: any) => ({
          ...invoice,
          invoiceType: 'Standard'
        }));
        setInvoices(invoicesWithType);
        if (result.data.length === 0) {
          setFetchError('لم يتم العثور على فواتير غير مدفوعة');
        }
      } else {
        setFetchError('فشل في جلب الفواتير');
      }
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      setFetchError('فشل في جلب الفواتير');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (paymentScheduleId: any, remainingAmount: number) => {
    const isAlreadySelected = selectedInvoices.find(inv => inv.id === paymentScheduleId);

    if (isAlreadySelected) {
      setSelectedInvoices(selectedInvoices.filter(inv => inv.id !== paymentScheduleId));
    } else {
      setSelectedInvoices([...selectedInvoices, { id: paymentScheduleId, amount: remainingAmount }]);
    }
  };

  const handleAmountChange = (paymentScheduleId: any, amount: number) => {
    const numericAmount = Number(amount) || 0;
    const invoice = invoices.find(inv => inv.apPaymentScheduleId === paymentScheduleId);

    if (!invoice) return;

    const safeAmount = Math.min(Math.max(numericAmount, 0), Number(invoice.remainingAmount) || 0);

    setSelectedInvoices(prev => prev.map(inv => (inv.id === paymentScheduleId ? { ...inv, amount: safeAmount } : inv)));
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      const allInvoices = invoices.map(invoice => ({
        id: invoice.apPaymentScheduleId,
        amount: invoice.remainingAmount
      }));
      setSelectedInvoices(allInvoices);
    }
  };

  const handleSubmit = () => {
    const filteredInvoices = invoices
      .filter((f: any) => selectedInvoices.some(selected => selected.id === f.apPaymentScheduleId))
      .map((item: any) => {
        const selectedInvoice = selectedInvoices.find(selected => selected.id === item.apPaymentScheduleId);
        const paymentAmount = selectedInvoice?.amount || 0;
        return {
          id: null,
          apPaymentScheduleId: item.apPaymentScheduleId,
          invoiceNum: item.invoiceNum,
          invoiceDate: item.invoiceDate,
          dueDate: item.dueDate,
          invoiceAmount: item.invoiceAmount,
          schadualAmount: item.amount,
          amount: paymentAmount,
          actualRemainingAmount: item.remainingAmount,
          invoiceType: item.invoiceType
        };
      });
    formik?.setFieldValue('scheduleList', filteredInvoices);
    onClose();
  };

  // التحقق مما إذا كانت الفاتورة محددة
  const isInvoiceSelected = (paymentScheduleId: any) => {
    return selectedInvoices.some(inv => inv.id === paymentScheduleId);
  };

  // الحصول على مبلغ الفاتورة المحددة
  const getSelectedInvoiceAmount = (paymentScheduleId: any) => {
    const selectedInvoice = selectedInvoices.find(inv => inv.id === paymentScheduleId);
    return selectedInvoice?.amount || 0;
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    // return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    // ترتيب تصاعدي (من الأقدم إلى الأحدث) حسب تاريخ الفاتورة
  return new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime();
  });

  const totalSelectedAmount = selectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const isFormValid = selectedInvoices.length > 0 && selectedInvoices.every(invoice => invoice.amount > 0);
  const hasValidationErrors = selectedInvoices.some(invoice => invoice.amount === 0);

  return (
    <div>
      <div className='row g-3 mb-4'>
        <div className='col-lg-6'>
          <div className='form-group'>
            <label className='form-label fw-bold'>المورد</label>
            <TreeSelect
              showSearch
              className='w-100'
              treeLine
              placeholder='اختر المورد'
              allowClear
              treeDefaultExpandAll={false}
              value={values.peopleId}
              onChange={option => {
                setSelectedInvoices([]);
                setInvoices([]);
                formik.setFieldValue('peopleId', option === null ? null : option);
              }}
              treeData={treeSuppliersData}
              treeNodeFilterProp='title'
              getPopupContainer={triggerNode => triggerNode.parentNode}
            />
          </div>
        </div>

        <div className='col-lg-4'>
          <div className='form-group'>
            <label className='form-label fw-bold'>الوحدة التشغيلية</label>
            <Select
              isSearchable={true}
              isClearable
              options={OperatingUnitOptions}
              value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId)}
              onChange={(option: any) => {
                setSelectedInvoices([]);
                setInvoices([]);
                setFieldValue('organizationId', option === null ? null : option.value);
              }}
              placeholder='اختر الوحدة التشغيلية'
              className='react-select-container'
              classNamePrefix='react-select'
            />
          </div>
        </div>

        <div className='col-lg-2'>
          <div className='form-group h-100 d-flex flex-column justify-content-end'>
            <button
              disabled={isLoading || !values.organizationId || !values.peopleId}
              type='button'
              className='btn btn-sm btn-outline-theme w-100 py-2'
              onClick={handleGetUnpaidInvoices}
            >
              {isLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                  جاري التحميل...
                </>
              ) : (
                <>
                  <i className='fas fa-download me-2'></i>
                  جلب الفواتير
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {fetchError && (
        <div className='alert alert-warning d-flex align-items-center mb-4' role='alert'>
          <i className='fas fa-exclamation-triangle me-2'></i>
          <div>{fetchError}</div>
        </div>
      )}

      {sortedInvoices.length > 0 && (
        <PrepaymentsAlert peopleId={values.peopleId} organizationId={values.organizationId} />
      )}

      {sortedInvoices.length > 0 && (
        <Card className='mb-4'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h6 className='mb-0'>
              <i className='fas fa-file-invoice me-2'></i>
              قائمة الفواتير غير المدفوعة
            </h6>
            <div>
              <span className='badge bg-theme me-2'>الفواتير المحددة: {selectedInvoices.length}</span>
              <span className='badge bg-success'>الإجمالي: {totalSelectedAmount.toLocaleString()}</span>
            </div>
          </div>

          <div className='card-body p-0'>
            <div className='table-responsive' style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <table className='table table-hover mb-0'>
                <thead
                  className='fw-bold'
                  style={{ position: 'sticky', top: 0, zIndex: 1, backdropFilter: 'blur(20px)' }}
                >
                  <tr>
                    <th className='text-center small'>
                      <div className='form-check'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={selectedInvoices.length > 0 && selectedInvoices.length === invoices.length}
                          onChange={handleSelectAll}
                          disabled={invoices.length === 0}
                        />
                      </div>
                    </th>
                    <th className='text-center small'>رقم الدفع</th>
                    <th className='text-center small'>رقم الفاتورة</th>
                    <th className='text-center small'>تاريخ الفاتورة</th>
                    <th className='text-center small'>مبلغ الفاتورة</th>
                    <th className='text-center small'>المبلغ المتبقي</th>
                    <th className='text-center small'>المبلغ المراد دفعه</th>
                    <th className='text-center small'>تاريخ الاستحقاق</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map((invoice, index) => {
                    const isSelected = isInvoiceSelected(invoice.apPaymentScheduleId);
                    const paymentAmount = getSelectedInvoiceAmount(invoice.apPaymentScheduleId);

                    return (
                      <tr key={invoice.apPaymentScheduleId} className={isSelected ? 'table-active' : ''}>
                        <td className='text-center align-middle'>
                          <div className='form-check'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={isSelected}
                              onChange={() =>
                                handleCheckboxChange(invoice.apPaymentScheduleId, invoice.remainingAmount)
                              }
                            />
                          </div>
                        </td>
                        <td className='text-center align-middle'>{invoice.paymentNum}</td>
                        <td className='text-center align-middle'>{invoice.invoiceNum}</td>
                        <td className='text-center align-middle'>{invoice.invoiceDate}</td>
                        <td className='text-center align-middle'>
                          <span className='fw-bold text-primary'>{Number(invoice.invoiceAmount).toLocaleString()}</span>
                        </td>
                        <td className='text-center align-middle'>
                          <span className='fw-bold text-success'>
                            {Number(invoice.remainingAmount).toLocaleString()}
                          </span>
                        </td>
                        <td className='text-center align-middle'>
                          {isSelected ? (
                            <div className='d-flex align-items-center gap-2 justify-content-center'>
                              <NumericFormat
                                className='form-control form-control-sm text-center'
                                style={{ width: '120px' }}
                                value={paymentAmount}
                                thousandSeparator=',' // فواصل آلاف
                                decimalSeparator='.' // العرض الافتراضي
                                allowedDecimalSeparators={[',', '.']} // ✅ يقبل الفاصلة أو النقطة كعشري أثناء الكتابة
                                decimalScale={2}
                                allowNegative={false}
                                // يمنع تجاوز remainingAmount أثناء الكتابة
                                isAllowed={({ floatValue }) => {
                                  const v = floatValue ?? 0;
                                  return v >= 0 && v <= Number(invoice.remainingAmount || 0);
                                }}
                                onValueChange={({ floatValue }) => {
                                  handleAmountChange(invoice.apPaymentScheduleId, floatValue ?? 0);
                                }}
                              />
                            </div>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                        <td className='text-center align-middle'>{invoice.dueDate?.toString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-between align-items-center'>
            <div className='text-muted small'>
              عرض {sortedInvoices.length} من {sortedInvoices.length} فاتورة
            </div>
            <div className='text-theme fw-bold'>المبلغ الإجمالي المحدد: {totalSelectedAmount.toLocaleString()}</div>
          </div>
        </Card>
      )}

      {hasValidationErrors && (
        <div className='alert alert-warning d-flex align-items-center mb-4' role='alert'>
          <i className='fas fa-exclamation-triangle me-2'></i>
          <div>يجب إدخال مبلغ أكبر من الصفر للفواتير المحددة</div>
        </div>
      )}

      <div className='border-top mt-4 pt-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            {isFormValid && (
              <div className='alert alert-success d-flex align-items-center py-2 mb-0' role='alert'>
                <i className='fas fa-check-circle me-2'></i>
                <div>جاهز لدفع الفواتير المحددة</div>
              </div>
            )}
          </div>

          <div className='d-flex'>
            <button disabled={!isFormValid} className='btn btn-theme me-2 px-4' onClick={handleSubmit}>
              <i className='fas fa-money-bill me-2'></i>
              دفع الفواتير المحددة
            </button>

            <button
              type='button'
              className='btn btn-outline-secondary'
              onClick={() => navigate('/AP/ApPayments', { replace: true })}
            >
              <i className='fas fa-times me-2'></i>
              إلغاء
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

////////////////////////////////////////////////////////

interface PrepaymentsAlertProps {
  peopleId: string;
  organizationId: string;
}

export const PrepaymentsAlert: React.FC<PrepaymentsAlertProps> = ({ peopleId, organizationId }) => {
  const { t } = useTranslation();

  // استخدام الهوك داخل المكون
  const {
    hasPrepayments,
    totalPrepaymentAmount,
    UnApplyPrepaymentsList,
    loading: prepaymentsLoading,
    error: prepaymentsError
  } = useGetUnApplyPrepayments({
    PeopleId: peopleId,
    organizationId: organizationId
  });

  if (!peopleId) {
    return null;
  }

  return (
    <div>
      <Loading loading={prepaymentsLoading} error={prepaymentsError} Type='Dots'>
        {hasPrepayments && (
          <div className='alert alert-info d-flex align-items-center' role='alert'>
            <i className='fas fa-info-circle me-2 fs-5 text-info'></i>
            <div className='flex-grow-1'>
              <div className='d-flex align-items-center flex-wrap'>
                <span className='me-2'>
                  <strong>{t('Reminder')} : </strong>
                  {t('PrepaymentMessage')}
                </span>
                <span className='fw-bold text-success'>
                  {/* {totalPrepaymentAmount?.toLocaleString() || 0} {t('Currency')} */}
                  {totalPrepaymentAmount?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          </div>
        )}
      </Loading>
    </div>
  );
};
////////////////////////////////////////////////////////
